package one.devsbox.www.ui.locations

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ChevronRight
import compose.assets.svg.LocationOutline
import compose.assets.svg.Plus
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.CategorySplashUI
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.SearchBox
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tk.mallumo.utils.noDiacriticsLower

@Composable
@ComposableNavNode
fun ListingLocationUI() {
    with(ListingLocationCtx.get()) {
        ContentListingLocationUI(Modifier.fillMaxSize())
    }
}

@Composable
context(ctx: ListingLocationCtx)
private fun ContentListingLocationUI(modifier: Modifier = Modifier) {
    Column(modifier = modifier.padding(16.dp)) {
        Crossfade(targetState = ctx.vm.locationState) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reloadLocations() }
                    )
                }

                is DataState.Result -> {
                    SuccessUI(state.nnvl)
                }
            }
        }
    }
}

@Composable
context(ctx: ListingLocationCtx)
private fun SuccessUI(locations: SharedIO.Locations) {
    val list = locations.items
    var expandedId by remember { mutableStateOf<Long?>(null) }
    val dp = LocalDisplayProvider.current.state

    Box(Modifier.fillMaxSize()) {
        if (locations.count == 0) {
            CategorySplashUI(
                icon = Svg.LocationOutline,
                title = Res.string.ui_locations_splash_title,
                explanation = Res.string.ui_locations_splash_desc,
                actions = ctx.vm.contentActions
            )
        } else {
            Column(
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    SearchBox(
                        state = ctx.vm.search,
                        modifier = Modifier.weight(1f),
                        hint = stringResource(Res.string.ui_locations_listing_search_placeholder)
                    )
                }
                Spacer(Modifier.size(16.dp))

                val filteredList = remember(list, ctx.vm.search.text.toString()) {
                    val value = ctx.vm.search.text.toString().noDiacriticsLower
                    list.filter {
                        value.isEmpty() || it.name.noDiacriticsLower.contains(value) || it.address.noDiacriticsLower.contains(
                            value
                        )
                    }.sortedByDescending { it.id }
                }

                val showingStart = if (filteredList.isEmpty()) 0 else 1
                val showingEnd = filteredList.size
                val totalCount = list.size

                Text(
                    text = stringResource(
                        Res.string.ui_locations_listing_showing,
                        showingStart,
                        showingEnd,
                        totalCount
                    ),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                    letterSpacing = 1.sp,
                    modifier = Modifier.padding(bottom = 16.dp)
                )

                if (filteredList.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().weight(1f),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Icon(
                                imageVector = Svg.LocationOutline,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                                modifier = Modifier.size(48.dp)
                            )
                            Spacer(Modifier.height(16.dp))
                            Text(
                                text = stringResource(Res.string.ui_campaigns_edit_no_locations_found),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                            )
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        contentPadding = PaddingValues(bottom = 250.dp)
                    ) {
                        if (!dp.smallScreen) {
                            item {
                                Row(
                                    modifier = Modifier.fillMaxWidth()
                                        .padding(horizontal = 16.dp, vertical = 8.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        stringResource(Res.string.ui_locations_listing_header_id),
                                        Modifier.width(60.dp),
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Text(
                                        stringResource(Res.string.ui_locations_listing_header_name_address),
                                        Modifier.weight(1f),
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Text(
                                        stringResource(Res.string.ui_locations_listing_header_devices),
                                        Modifier.width(80.dp),
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Spacer(Modifier.width(60.dp))
                                }
                            }
                            item {
                                HorizontalDivider(Modifier.padding(bottom = 8.dp))
                            }
                        }
                        items(filteredList) { location ->
                            LocationRow(
                                location = location,
                                isExpanded = expandedId == location.id,
                                onExpandClick = {
                                    expandedId =
                                        if (expandedId == location.id) null else location.id
                                    if (expandedId != null) {
                                        ctx.action.fetchLocationDetails(location.id)
                                    }
                                }
                            )
                        }
                    }
                }
            }
            ContentActions(
                contentActions = ctx.vm.contentActions,
                colorInactive = ctx.colorSecondaryContainer,
                colorActive = ctx.colorPrimaryContainer
            )
        }
    }
}

@Composable
context(ctx: ListingLocationCtx)
private fun LocationRow(
    location: SharedIO.LocationSimple,
    isExpanded: Boolean,
    onExpandClick: () -> Unit
) {
    val details = ctx.vm.locationDetails[location.id]
    val dp = LocalDisplayProvider.current.state

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(4.dp)
            )
            .background(Color.Transparent)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (dp.smallScreen) {
                Row(
                    modifier = Modifier
                        .weight(1f)
                        .clickable { onExpandClick() }
                        .padding(horizontal = 12.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(Modifier.weight(1f)) {
                        Text(
                            location.name,
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        if (location.address.isNotEmpty()) {
                            Text(
                                location.address,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                        if (ctx.me.info.adminMode) {
                            Text(
                                text = location.nameCompany,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.secondary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }
                Text(
                    text = "${location.devices}x",
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.align(Alignment.CenterVertically)
                        .padding(start = 8.dp, end = 16.dp)
                )

                VerticalDivider(modifier = Modifier.height(24.dp))
                Box(
                    modifier = Modifier
                        .size(48.dp)
                        .clickable { ctx.action.openDetail(location.id) },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Svg.ChevronRight, null, modifier = Modifier.size(20.dp))
                }
            } else {
                Row(
                    modifier = Modifier.weight(1F).clickable { onExpandClick() }.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = location.id.toString(36).padStart(4, '0').uppercase(),
                        modifier = Modifier.width(60.dp),
                        color = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                        fontWeight = FontWeight.Bold,
                        fontFamily = FontFamily.Monospace
                    )
                    Column(Modifier.weight(1f)) {
                        Text(location.name, fontWeight = FontWeight.Bold)
                        if (location.address.isNotEmpty()) {
                            Text(
                                location.address,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                        if (ctx.me.info.adminMode) {
                            Text(
                                text = location.nameCompany,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.secondary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                    Text(
                        text = location.devices.toString().padStart(2, '0'),
                        modifier = Modifier.width(80.dp),
                        color = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                        fontFamily = FontFamily.Monospace,
                        textAlign = TextAlign.Center
                    )
                }
                VerticalDivider(modifier = Modifier.height(32.dp))
                Box(Modifier.size(70.dp).clickable {
                    ctx.action.openDetail(location.id)
                }, contentAlignment = Alignment.Center) {
                    Icon(Svg.ChevronRight, null)
                }
            }
        }

        AnimatedVisibility(visible = isExpanded) {
            if (details == null) {
                Box(Modifier.fillMaxWidth().height(100.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(modifier = Modifier.size(24.dp))
                }
            } else {
                LocationDetails(details)
            }
        }
    }
}
